/**
 * Amplitude Analytics and Session Replay Configuration
 *
 * This module handles the initialization of Amplitude Analytics and Session Replay
 * for the Meetball application. It ensures Amplitude is only initialized once
 * during the application lifecycle.
 *
 * All Amplitude requests are routed through a reverse proxy to avoid ad-blockers.
 * @see docs/amplitude-proxy.md for full architecture documentation
 */

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { logger } from '@utils/logger';

import { urlSecretRedactionPlugin } from './analytics/urlSecretRedactionPlugin';
import { sessionReplayUgcFilterRules } from './analytics/urlSecrets';

let isInitialized = false;
// Sticky flag set when initializeAmplitude finalizes WITHOUT successfully
// initializing the SDK (config-disabled, missing API key, or thrown init
// error). Once set, subscribeAmplitudeReady returns a no-op unsubscribe
// without queueing the listener, so callers don't accumulate closures
// inside readyListeners that would never fire.
let initFailed = false;

// One-shot subscribers waiting for Amplitude to finish its lazy init.
// Drained inside initializeAmplitude() once `isInitialized` flips to true.
// On the failure path, the set is also cleared without invoking listeners
// to clean up any subscribers that beat init to the punch.
const readyListeners = new Set<() => void>();

/**
 * Drop all pending ready listeners without invoking them. Used by both the
 * success path (after each callback has fired) and the failure paths
 * (config-disabled, no API key, init threw) to release closures.
 */
const drainReadyListeners = (): (() => void)[] => {
  const callbacks = Array.from(readyListeners);
  readyListeners.clear();
  return callbacks;
};

/**
 * Initialize Amplitude Analytics with Session Replay
 * This function should be called once at the application root
 */
export const initializeAmplitude = (): void => {
  // Prevent multiple initializations
  if (isInitialized) {
    logger.warn('Amplitude is already initialized');
    return;
  }

  try {
    const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
    const environment = import.meta.env.VITE_APP_ENV;
    const enableAmplitude = import.meta.env.VITE_ENABLE_AMPLITUDE === 'true';

    // Don't initialize Amplitude in dev environment to avoid data collision with production
    // Can be overridden with VITE_ENABLE_AMPLITUDE=true for testing proxy configuration
    if (environment === 'dev' && !enableAmplitude) {
      logger.info(
        'Amplitude Analytics disabled in dev environment to prevent data collision with production',
      );
      // Mark init as finalized-failed so any pending listeners are cleared
      // and subsequent subscribeAmplitudeReady calls return a no-op.
      initFailed = true;
      drainReadyListeners();
      return;
    }

    if (!apiKey) {
      // logger.error (always logged in production) — a missing API key in a
      // non-dev environment is a real misconfiguration that operators need
      // to see immediately. logger.warn was silent in prod.
      logger.error(
        'Amplitude API key not found in environment variables. Analytics will not be initialized.',
      );
      initFailed = true;
      drainReadyListeners();
      return;
    }

    // Add Session Replay plugin with 100% sample rate
    // configServerUrl routes config requests through our proxy to avoid ad blockers
    // trackServerUrl routes session replay data through our proxy to avoid ad blockers
    const proxyUrl = `${window.location.origin}/amplitude`;
    // interactionConfig carries the UGC filter rules that keep an invite token
    // out of the recorded URL. `enabled`/`batch` are required by the option
    // type but are not ours to choose: the SDK replaces the whole
    // interactionConfig with the project's remote `sr_interaction_config` and
    // only re-attaches ugcFilterRules, so interaction tracking stays off unless
    // the Amplitude project turns it on. See docs/onboarding/onboarding-design.md
    // for what that leaves uncovered.
    amplitude.add(
      sessionReplayPlugin({
        sampleRate: 1,
        configServerUrl: `${proxyUrl}/config`,
        trackServerUrl: `${proxyUrl}/sessions/v2/track`,
        interactionConfig: {
          enabled: false,
          batch: false,
          ugcFilterRules: sessionReplayUgcFilterRules(),
        },
      }),
    );

    // Initialize Amplitude with the API key from environment variables
    // Uses reverse proxy at /amplitude to route to EU endpoints, avoiding ad blockers
    // remoteConfig.serverUrl routes autocapture config requests through proxy
    // enableDiagnostics disabled - as of 18-01-2026, SDK telemetry endpoint cannot be proxied
    const initResult = amplitude.init(apiKey, {
      autocapture: true,
      serverUrl: proxyUrl,
      remoteConfig: {
        serverUrl: `${proxyUrl}/config`,
      },
      enableDiagnostics: false,
    });

    // Registered AFTER init resolves, deliberately. Enrichment plugins run in
    // registration order, and init() registers the SDK's own page-URL
    // enrichment plugin (the one that stamps the tokenised `[Amplitude] Page
    // Location` onto every event) last among its built-ins. Adding this before
    // init would put it ahead of that plugin, and the redaction would be
    // overwritten by the value it was meant to replace.
    void initResult.promise
      .then(() => amplitude.add(urlSecretRedactionPlugin()).promise)
      .catch((error: unknown) => {
        // logger.error (always logged): without this plugin the invite token in
        // the address bar ships to Amplitude in full, so a silent failure here
        // is a silent data leak.
        logger.error('Failed to register the Amplitude URL redaction plugin:', error);
      });

    isInitialized = true;
    logger.info('Amplitude Analytics and Session Replay initialized successfully');

    // Drain one-shot ready listeners. Snapshot first, clear, then call so
    // a listener that re-subscribes inside its callback does not get
    // re-invoked in the same drain pass. Errors in any single callback
    // do not block the rest.
    const callbacks = drainReadyListeners();
    callbacks.forEach(cb => {
      try {
        cb();
      } catch (e) {
        logger.error('amplitude: ready callback failed', e);
      }
    });
  } catch (error) {
    logger.error('Failed to initialize Amplitude:', error);
    // Init threw mid-sequence: mark finalized-failed and drop pending
    // listeners so they do not accumulate forever. Their contract is
    // 'fire on success', and we've now confirmed success will not happen.
    initFailed = true;
    drainReadyListeners();
  }
};

/**
 * Get the Amplitude instance
 * Useful for custom event tracking throughout the application
 */
export const getAmplitude = () => amplitude;

/**
 * Check if Amplitude is initialized
 */
export const isAmplitudeInitialized = (): boolean => isInitialized;

/**
 * Subscribe to Amplitude readiness. If already initialized, the callback
 * fires synchronously and the returned unsubscribe is a no-op. Otherwise
 * it fires once when initializeAmplitude() completes successfully, then
 * auto-unsubscribes. Returns an unsubscribe function for early cleanup
 * (e.g., on component unmount before init).
 *
 * Use this for one-shot events that may be triggered before Amplitude's
 * deferred init completes (cold-start path). Without it, the event is
 * silently dropped because the firing useEffect's deps array does not
 * change when init later finishes.
 *
 * Init-failed semantics: if initializeAmplitude() finalized without
 * successfully initializing the SDK (config-disabled, missing API key,
 * thrown error), the listener is NOT queued and the unsubscribe is a
 * no-op. This prevents closures from accumulating in misconfigured
 * preview environments where users mount a component that wants to
 * report telemetry but the SDK will never come online.
 */
export const subscribeAmplitudeReady = (cb: () => void): (() => void) => {
  if (isInitialized) {
    // Symmetric error handling with the drain path in initializeAmplitude:
    // a throwing callback must not propagate to the caller. Otherwise the
    // sync-fire branch (init already done) and the deferred branch (drained
    // on init) would have different contracts depending on timing, which
    // is exactly the kind of subtle bug that surfaces in production six
    // months later.
    try {
      cb();
    } catch (e) {
      // logger.error (always logged) to match the deferred drain path
      // above. logger.warn is dev-only here, so a production callback
      // failure on the sync-fire branch would be silently swallowed
      // while the same failure on the deferred branch would surface.
      logger.error('amplitude: ready callback failed', e);
    }
    return () => {
      /* noop: already fired */
    };
  }
  if (initFailed) {
    return () => {
      /* noop: SDK will never come online; nothing to unsubscribe */
    };
  }
  readyListeners.add(cb);
  return () => {
    readyListeners.delete(cb);
  };
};

/**
 * Get the Amplitude device ID for session linking with backend
 * AIDEV-NOTE: Used by API client to send X-Amplitude-Device-Id header
 * This enables backend events to be linked to the same user session
 */
export const getAmplitudeDeviceId = (): string | undefined => {
  if (!isInitialized) {
    return undefined;
  }
  try {
    return amplitude.getDeviceId();
  } catch (error) {
    logger.warn('Failed to get Amplitude device ID:', error);
    return undefined;
  }
};

// Export the amplitude instance for direct use if needed
export { amplitude };
